/**
 * Adapter between the API client shape (raw Prisma) and the flattened UI `Client`.
 *
 * The clients endpoint does not expose money rollups (revenue, ad-spend) or the
 * single `service` the UI shows. Those come from payments/wallets/ad-spend and
 * the clientServices relation (Phase 5.2), so they default here.
 */

import type { Client, ClientLevel, ClientStatus, ClientType, ContractModel, Currency, ServiceType } from "@/types";
import type { ClientFormValues } from "./components/ClientFormModal";

export interface ApiClientService {
  service?: { type?: ServiceType | null } | null;
}

export interface ApiWallet {
  balanceUsd?: number | string | null;
}

/** Fields of the API client we read. List returns scalars; detail adds relations. */
export interface ApiClient {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  type: ClientType;
  niche?: string | null;
  level?: ClientLevel | null;
  status: ClientStatus;
  contractModel: ContractModel;
  monthlyFee?: number | string | null;
  currency?: Currency;
  startDate?: string | null;
  notes?: string | null;
  createdAt?: string | null;
  createdById?: string | null;
  wallet?: ApiWallet | null;
  clientServices?: ApiClientService[] | null;
}

const DEFAULT_SERVICE: ServiceType = "MEDIA_BUYER";

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatSince(api: ApiClient): string {
  const raw = api.startDate ?? api.createdAt;
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/** Map an API client to the flattened UI Client. */
export function toClient(api: ApiClient): Client {
  return {
    id: api.id,
    name: api.name,
    email: api.email ?? undefined,
    phone: api.phone ?? undefined,
    facebookUrl: api.facebookUrl ?? undefined,
    instagramUrl: api.instagramUrl ?? undefined,
    tiktokUrl: api.tiktokUrl ?? undefined,
    type: api.type,
    niche: api.niche ?? undefined,
    level: api.level ?? undefined,
    status: api.status,
    contractModel: api.contractModel,
    // List endpoint omits clientServices, so list rows show the default service.
    // The detail endpoint provides the real one.
    service: api.clientServices?.[0]?.service?.type ?? DEFAULT_SERVICE,
    monthlyFee: toNumber(api.monthlyFee),
    currency: api.currency ?? "DZD",
    startDate: api.startDate ?? undefined,
    notes: api.notes ?? undefined,
    revenue: 0,
    adSpendUsd: 0,
    walletUsd: toNumber(api.wallet?.balanceUsd),
    ownerId: api.createdById ?? undefined,
    createdAt: api.createdAt ?? undefined,
    since: formatSince(api),
  };
}

/**
 * Map UI form values to the API create/update body.
 *
 * `service` is intentionally omitted: the API models services as a relation
 * (`clientServices`), not a scalar field, so per-client service assignment is
 * handled separately.
 */
export function toClientWriteBody(values: ClientFormValues): Record<string, unknown> {
  return {
    name: values.name.trim(),
    email: blankToNull(values.email),
    phone: blankToNull(values.phone),
    facebookUrl: blankToNull(values.facebookUrl),
    instagramUrl: blankToNull(values.instagramUrl),
    tiktokUrl: blankToNull(values.tiktokUrl),
    type: values.type,
    niche: blankToNull(values.niche),
    level: values.level,
    status: values.status,
    contractModel: values.contractModel,
    monthlyFee: values.monthlyFee,
    currency: values.currency,
    startDate: blankToNull(values.startDate),
    notes: blankToNull(values.notes),
  };
}
